import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { trackEvent } from "../utils/analytics";
import FETCHER from "./_fetcher";
import { IResponse } from "types/api";
import { IFavorites } from "types/favorite";

export const getFavoriteList = async (page?: number, limit?: number) => {
  try {
    const result = await FETCHER.get<IResponse<IFavorites[]>>(
      `/favorites?page=${page || 1}&limit=${limit || 20}`
    );
    return result.data;
  } catch (e) {
    console.log(e);
  }
};
export const useGetFavoriteList = (page?: number, limit?: number) => {
  const { data, isLoading } = useQuery({
    queryKey: ["getFavorite", page, limit],
    queryFn: () => getFavoriteList(page, limit),
    placeholderData: keepPreviousData,
  });

  const productList = data?.items.map((favorite) => ({
    ...favorite.product, // Spread product details from favorite
    isFavorite: true, // Mark as favorite
  }));

  return {
    favoriteList: data
      ? {
          items: productList,
          meta: data?.meta,
        }
      : undefined,
    isLoading,
  };
};

export const addFavorite = async (productId: number) => {
  const result = await FETCHER.post(`/favorites/${productId}`);
  return result;
};

export const useAddFavorite = () => {
  const { data, mutateAsync, isPending } = useMutation({
    mutationFn: addFavorite,
    onSuccess: (data, productId) => {
      // Track successful add to favorite event
      trackEvent("Add to Favorites", {
        product_id: productId,
        timestamp: new Date().toISOString(),
        status: "success"
      });
    },
    onError: (error, productId) => {
      // Track failed add to favorite event
      trackEvent("Add to Favorites", {
        product_id: productId,
        timestamp: new Date().toISOString(),
        status: "failed",
        error_message: error instanceof Error ? error.message : "Unknown error"
      });
    }
  });
  return {
    data: data,
    addFavoriteAsync: mutateAsync,
    isPending,
  };
};

export const deleteFavorite = async (productId: number) => {
  const result = await FETCHER.delete(`/favorites/${productId}`);
  return result;
};

export const useDeleteFavorite = () => {
  const queryClient = useQueryClient();

  const { data, mutateAsync, isPending } = useMutation({
    mutationFn: deleteFavorite,
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ["getFavorite"],
      });

      queryClient.invalidateQueries({
        queryKey: ["getProduct"],
      });
    },
  });
  return {
    data: data,
    deleteFavoriteAsync: mutateAsync,
    isPending,
  };
};
